import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Clock, Calendar, RefreshCw } from "lucide-react";

const WellnessTasksList = ({ tasks, onComplete, color }) => {
  const [completedIds, setCompletedIds] = React.useState(new Set());

  if (!tasks || tasks.length === 0) {
    return (
      <p className="text-gray-500 text-sm italic">
        No tasks yet. Chat with your coach to create some!
      </p>
    );
  }

  // Filter out tasks that have been completed (with animation delay)
  const visibleTasks = tasks.filter(
    (t) => !completedIds.has(t.instance?.id) && !t.instance?.completed
  );

  if (visibleTasks.length === 0) {
    return (
      <p className="text-gray-500 text-sm italic">
        All tasks completed! Great job!
      </p>
    );
  }

  const handleComplete = async (instanceId) => {
    // Add to completed set immediately for animation
    setCompletedIds((prev) => new Set([...prev, instanceId]));
    // Then call the actual completion
    await onComplete(instanceId);
  };

  return (
    <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
      <AnimatePresence mode="popLayout">
        {visibleTasks.map((taskWithInstance) => (
          <TaskItem
            key={taskWithInstance.instance?.id || taskWithInstance.task.id}
            taskWithInstance={taskWithInstance}
            onComplete={handleComplete}
            color={color}
          />
        ))}
      </AnimatePresence>
    </div>
  );
};

const TaskItem = ({ taskWithInstance, onComplete, color }) => {
  const { task, instance } = taskWithInstance;
  const [isCompleting, setIsCompleting] = React.useState(false);

  const handleComplete = async () => {
    if (!instance || instance.completed || isCompleting) return;
    setIsCompleting(true);
    await onComplete(instance.id);
  };

  const formatScheduledDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    }
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{
        opacity: 0,
        x: 20,
        scale: 0.95,
        transition: { duration: 0.3 }
      }}
      className="rounded-lg p-3 border transition-colors bg-gray-800/50 border-gray-700 hover:border-gray-600"
    >
      <div className="flex items-start gap-3">
        {/* Completion checkbox */}
        <button
          onClick={handleComplete}
          disabled={isCompleting || !instance}
          className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all flex-shrink-0 ${
            isCompleting
              ? "bg-green-500 border-green-500"
              : "border-gray-600 hover:border-green-500 hover:bg-green-500/10"
          }`}
          title="Mark as complete"
        >
          {isCompleting && (
            <Check size={12} className="text-white" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <h5 className="font-medium text-sm text-white">
            {task.title}
          </h5>

          {task.description && (
            <p className="text-xs mt-1 line-clamp-2 text-gray-400">
              {task.description}
            </p>
          )}

          {/* Schedule info */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            {task.isRecurring && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <RefreshCw size={10} />
                Recurring
              </span>
            )}
            {instance?.scheduledFor && (
              <>
                <span className="flex items-center gap-1 text-xs text-gray-500">
                  <Calendar size={10} />
                  {formatScheduledDate(instance.scheduledFor)}
                </span>
                {task.scheduledTime && (
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <Clock size={10} />
                    {formatTime(instance.scheduledFor)}
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WellnessTasksList;
